// Lo que se elige en el mapa al comprar: asientos sueltos (de filas o sillas
// de mesa) con su tarifa, mesas enteras con su paquete y adicionales, las
// entradas sugeridas que se suman desde una mesa y las cantidades de las
// ubicaciones generales dibujadas en el plano.

export const MESA_ENTERA = "mesa";

export const pedidoVacio = () => ({ asientos: {}, mesas: {}, sugeridas: {}, generales: {} });

export const indexar = (mapa) => {
  const tarifas = {};
  mapa.tarifas.forEach((t) => {
    tarifas[t.id] = t;
  });
  const adicionales = new Set(mapa.tarifas.flatMap((t) => t.adicionales.map((a) => a.idtarifa)));
  const sectores = {};
  const mesas = {};
  const asientos = {};
  mapa.sectores.forEach((s) => {
    sectores[s.id] = s;
    s.filas.forEach((f) => f.asientos.forEach((a) => (asientos[a.id] = { asiento: a, sector: s, fila: f.nombre })));
    s.mesas.forEach((m) => {
      mesas[m.id] = { mesa: m, sector: s };
      m.sillas.forEach((a) => (asientos[a.id] = { asiento: a, sector: s, mesa: m }));
    });
  });
  return { mapa, tarifas, adicionales, sectores, mesas, asientos };
};

export const conLugares = (sector) => sector.filas.length > 0 || sector.mesas.length > 0;

// una ubicación general dibujada en el plano: se elige cantidad, sin lugar
export const esGeneralEnPlano = (sector) => sector.en_mapa && !conLugares(sector) && sector.tarifas.length > 0;

export const tarifasGenerales = (indice, sector) =>
  sector.tarifas.map((id) => indice.tarifas[id]).filter((t) => t && t.disponible);

export const ponerGeneral = (pedido, idtarifa, cantidad) => ({
  ...pedido,
  generales: { ...pedido.generales, [idtarifa]: Math.max(0, cantidad) },
});

export const cantidadEnGeneral = (indice, pedido, sector) =>
  tarifasGenerales(indice, sector).reduce((total, t) => total + (pedido.generales[t.id] || 0), 0);

// tarifas del sector que se eligen silla por silla (los adicionales de una
// mesa no: sólo se suman a una mesa entera)
export const tarifasPorSilla = (indice, sector) =>
  sector.tarifas
    .map((id) => indice.tarifas[id])
    .filter((t) => t && t.venta_mesa !== MESA_ENTERA && !indice.adicionales.has(t.id));

export const tarifaMesaEntera = (indice, sector) =>
  sector.tarifas.map((id) => indice.tarifas[id]).find((t) => t && t.venta_mesa === MESA_ENTERA) || null;

export const paquetesQueEntran = (tarifa, mesa) => tarifa.paquetes.filter((p) => p.personas <= mesa.sillas.length);

export const mesaLibre = (mesa) => !mesa.vendida && mesa.sillas.length > 0;

export const precioMesa = (tarifa, idpaquete) => {
  const paquete = tarifa.paquetes.find((p) => p.id === idpaquete);
  return paquete ? paquete.precio : tarifa.precio;
};

export const servicio = (tarifa, precio) => (precio * (tarifa.porcentaje_servicio || 0)) / 100;

export const alternarAsiento = (pedido, idasiento, idtarifa) => {
  const asientos = { ...pedido.asientos };
  if (asientos[idasiento]) delete asientos[idasiento];
  else asientos[idasiento] = idtarifa;
  return { ...pedido, asientos };
};

export const asientosDeTarifa = (pedido, idtarifa) =>
  Object.entries(pedido.asientos)
    .filter(([, t]) => t === idtarifa)
    .map(([id]) => Number(id));

export const ponerMesa = (pedido, idmesa, mesa) => ({ ...pedido, mesas: { ...pedido.mesas, [idmesa]: mesa } });

export const quitarMesa = (pedido, idmesa) => {
  const mesas = { ...pedido.mesas };
  delete mesas[idmesa];
  return { ...pedido, mesas };
};

export const ponerSugerida = (pedido, idtarifa, cantidad) => ({
  ...pedido,
  sugeridas: { ...pedido.sugeridas, [idtarifa]: Math.max(0, cantidad) },
});

// sugeridasDe: las entradas que se ofrecen con las mesas elegidas
export const sugeridasDe = (indice, pedido) => {
  const ids = new Set();
  Object.values(pedido.mesas).forEach((m) => indice.tarifas[m.idtarifa]?.sugeridas.forEach((id) => ids.add(id)));
  return [...ids].map((id) => indice.tarifas[id]).filter(Boolean);
};

// renglones del resumen: cantidad, subtotal y servicio por tarifa
export const resumen = (indice, pedido) => {
  const renglones = {};
  const sumar = (tarifa, cantidad, precio, detalle) => {
    if (!tarifa || cantidad <= 0) return;
    const r = (renglones[tarifa.id] ||= { tarifa, cantidad: 0, subtotal: 0, servicio: 0, detalle: [] });
    r.cantidad += cantidad;
    r.subtotal += precio * cantidad;
    r.servicio += servicio(tarifa, precio) * cantidad;
    if (detalle) r.detalle.push(detalle);
  };

  Object.entries(pedido.asientos).forEach(([id, idtarifa]) => {
    const tarifa = indice.tarifas[idtarifa];
    const lugar = indice.asientos[id];
    sumar(tarifa, 1, tarifa?.precio || 0, lugar && etiquetaLugar(lugar));
  });
  Object.entries(pedido.mesas).forEach(([idmesa, m]) => {
    const tarifa = indice.tarifas[m.idtarifa];
    const mesa = indice.mesas[idmesa]?.mesa;
    const paquete = tarifa?.paquetes.find((p) => p.id === m.idpaquete);
    const nombre = mesa ? `${mesa.nombre}${paquete ? ` (${paquete.personas} personas)` : ""}` : "";
    sumar(tarifa, 1, tarifa ? precioMesa(tarifa, m.idpaquete) : 0, nombre);
    Object.entries(m.adicionales || {}).forEach(([idt, cantidad]) => {
      const adicional = indice.tarifas[idt];
      sumar(adicional, cantidad, adicional?.precio || 0, mesa?.nombre);
    });
  });
  Object.entries(pedido.sugeridas).forEach(([idt, cantidad]) => {
    const tarifa = indice.tarifas[idt];
    sumar(tarifa, cantidad, tarifa?.precio || 0);
  });
  Object.entries(pedido.generales).forEach(([idt, cantidad]) => {
    const tarifa = indice.tarifas[idt];
    sumar(tarifa, cantidad, tarifa?.precio || 0, indice.sectores[tarifa?.idubicacion]?.nombre);
  });

  const lista = Object.values(renglones);
  const total = lista.reduce((acc, r) => acc + r.subtotal + r.servicio, 0);
  return { renglones: lista, total };
};

export const etiquetaLugar = ({ asiento, sector, fila, mesa }) => {
  if (mesa) return `${sector.nombre} · ${mesa.nombre} · silla ${asiento.etiqueta}`;
  if (fila) return `${sector.nombre} · fila ${fila} · asiento ${asiento.etiqueta}`;
  return `${sector.nombre} · asiento ${asiento.etiqueta}`;
};

const seleccionVacia = () => ({ asientos: [], mesas: {} });

// aCambios traduce el pedido a la selección por tarifa que usa el carrito:
// { [idtarifa]: { cantidad, seleccion } }. Incluye en cero las tarifas
// editables que quedaron sin elegir, para limpiarlas.
export const aCambios = (indice, pedido, editables) => {
  const cambios = {};
  const de = (idtarifa) => (cambios[idtarifa] ||= { cantidad: 0, seleccion: seleccionVacia() });

  Object.entries(pedido.asientos).forEach(([id, idtarifa]) => {
    const c = de(idtarifa);
    c.cantidad += 1;
    c.seleccion.asientos.push(Number(id));
  });

  Object.entries(pedido.mesas).forEach(([idmesa, m]) => {
    const tarifa = indice.tarifas[m.idtarifa];
    const c = de(m.idtarifa);
    const precio = precioMesa(tarifa, m.idpaquete);
    c.cantidad += 1;
    c.seleccion.mesas[idmesa] = 1;
    c.seleccion.paquetes = { ...(c.seleccion.paquetes || {}), [idmesa]: m.idpaquete ?? null };
    c.seleccion.precioTotal = (c.seleccion.precioTotal || 0) + precio;
    c.seleccion.comisionTotal = (c.seleccion.comisionTotal || 0) + servicio(tarifa, precio);
    Object.entries(m.adicionales || {}).forEach(([idt, cantidad]) => {
      if (cantidad <= 0) return;
      const a = de(Number(idt));
      a.cantidad += cantidad;
      a.seleccion.mesas[idmesa] = cantidad;
    });
  });

  Object.entries(pedido.sugeridas).forEach(([idt, cantidad]) => {
    de(Number(idt)).cantidad += cantidad;
  });
  Object.entries(pedido.generales).forEach(([idt, cantidad]) => {
    de(Number(idt)).cantidad += cantidad;
  });

  editables.forEach((id) => de(id));
  return cambios;
};

// desdeSeleccion arma el pedido con lo que ya estaba elegido, para volver a
// abrir el mapa sin perderlo.
export const desdeSeleccion = (indice, asientosSeleccionados, cantidad) => {
  const pedido = pedidoVacio();
  Object.entries(asientosSeleccionados || {}).forEach(([idt, sel]) => {
    const idtarifa = Number(idt);
    const tarifa = indice.tarifas[idtarifa];
    if (!tarifa || !sel) return;
    (sel.asientos || []).forEach((id) => {
      if (indice.asientos[id]) pedido.asientos[id] = idtarifa;
    });
    if (tarifa.venta_mesa === MESA_ENTERA) {
      Object.keys(sel.mesas || {}).forEach((idmesa) => {
        pedido.mesas[idmesa] = { idtarifa, idpaquete: sel.paquetes?.[idmesa] ?? null, adicionales: {} };
      });
    }
  });
  Object.entries(asientosSeleccionados || {}).forEach(([idt, sel]) => {
    if (!indice.adicionales.has(Number(idt))) return;
    Object.entries(sel?.mesas || {}).forEach(([idmesa, n]) => {
      if (pedido.mesas[idmesa]) pedido.mesas[idmesa].adicionales[idt] = n;
    });
  });
  sugeridasDe(indice, pedido).forEach((t) => {
    if (cantidad?.[t.id] > 0) pedido.sugeridas[t.id] = cantidad[t.id];
  });
  indice.mapa.sectores.filter(esGeneralEnPlano).forEach((s) =>
    s.tarifas.forEach((idt) => {
      if (cantidad?.[idt] > 0) pedido.generales[idt] = cantidad[idt];
    })
  );
  return pedido;
};
